// Вкладка «Биржа» у звонильщика: что стало с заявками, которые он отдал.
// Главное здесь — СКОЛЬКО человек откликнулось и кто именно: на этом этапе мы
// меряем спрос, а кому отдать ученика, решает менеджер (решение Антона 30.07).
import { useState, useEffect, useCallback } from 'react'
import api from '../api'

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

function humanDate(iso) {
  if (!iso) return ''
  const d = new Date(iso + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`
}

function gradeLabel(grade) {
  const g = String(grade || '').trim()
  return /^\d+$/.test(g) ? `${g} класс` : g
}

/** «2026-07-30 10:56:12» → «30 июля, 10:56». База отдаёт время без «T». */
function humanMoment(raw) {
  if (!raw) return ''
  const s = String(raw).replace(' ', 'T')
  const d = new Date(s.endsWith('Z') ? s : s + 'Z')
  if (Number.isNaN(d.getTime())) return String(raw)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${hh}:${mm}`
}

function Badge({ offer }) {
  let text = 'Свободна'
  let style = { background: '#F0F9FF', color: '#075985', border: '1px solid #BAE6FD' }
  if (offer.status === 'cancelled') {
    text = 'Снята'
    style = { background: '#F9FAFB', color: '#667085', border: '1px solid #E4E7EC' }
  } else if (offer.status === 'taken') {
    text = `Отдана: ${offer.taken_by_name || 'преподавателю'}`
    style = { background: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0' }
  } else if (!offer.is_live) {
    text = 'Сутки вышли'
    style = { background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' }
  }
  return (
    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={style}>
      {text}
    </span>
  )
}

function OfferCard({ offer, onCancel, onAssign, busyId }) {
  const [openList, setOpenList] = useState(false)
  // Кого выбрали кнопкой «Отдать этому» — ждём подтверждения.
  const [confirmFor, setConfirmFor] = useState(null)
  const responders = offer.responders || []
  const canCancel = offer.status === 'open'
  // Отдать можно, пока заявка открыта — даже если сутки вышли: отклики-то есть.
  const canAssign = offer.status === 'open'
  const taken = offer.status === 'taken'

  return (
    <div className="rounded-2xl p-4 mb-3" style={{ border: '1px solid #E4E7EC', background: 'white' }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[15px] font-bold" style={{ color: '#101828' }}>
            {offer.subject}{offer.grade ? `, ${gradeLabel(offer.grade)}` : ''}
          </div>
          <div className="text-[13px] mt-1" style={{ color: '#475467' }}>
            Урок {humanDate(offer.slot_date)} в {offer.slot_begin_time}
            {offer.student_name ? ` · ученик ${offer.student_name}` : ''}
          </div>
          <div className="text-[12px] mt-1" style={{ color: '#667085' }}>
            Клиент {offer.client_price} ₽ · преподу {offer.teacher_price} ₽
          </div>
        </div>
        <Badge offer={offer} />
      </div>

      <div className="flex items-center gap-4 mt-3 pt-3 flex-wrap" style={{ borderTop: '1px solid #F2F4F7' }}>
        <div>
          <div className="text-[11px]" style={{ color: '#98A2B3' }}>Откликнулись</div>
          <div className="text-[20px] font-bold" style={{ color: responders.length ? '#0369A1' : '#98A2B3' }}>
            {responders.length}
          </div>
        </div>
        <div>
          <div className="text-[11px]" style={{ color: '#98A2B3' }}>Получили заявку</div>
          <div className="text-[20px] font-bold" style={{ color: '#101828' }}>{offer.sent_count}</div>
        </div>
        {offer.pending_count > 0 && (
          <div>
            <div className="text-[11px]" style={{ color: '#98A2B3' }}>Ждут очереди</div>
            <div className="text-[20px] font-bold" style={{ color: '#101828' }}>{offer.pending_count}</div>
          </div>
        )}
        <div className="flex-1" />
        {responders.length > 0 && (
          <button onClick={() => setOpenList(v => !v)}
            className="text-[13px] font-semibold px-3 py-2 rounded-lg"
            style={{ background: '#F0F9FF', color: '#0369A1' }}>
            {openList ? 'Скрыть' : 'Кто откликнулся'}
          </button>
        )}
        {canCancel && (
          <button onClick={() => onCancel(offer.id)} disabled={busyId === offer.id}
            className="text-[13px] font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
            style={{ background: 'white', color: '#667085', border: '1px solid #E4E7EC' }}>
            {busyId === offer.id ? 'Снимаем…' : 'Снять'}
          </button>
        )}
      </div>

      {openList && responders.length > 0 && (
        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #F2F4F7' }}>
          {responders.map((r, i) => {
            const won = taken && r.mk_id === offer.taken_by_mk_id
            return (
              <div key={r.mk_id}
                className="flex items-center justify-between gap-2 py-1.5 flex-wrap"
                style={won
                  ? { background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '7px 10px', margin: '0 -10px' }
                  : taken ? { opacity: 0.55 } : undefined}>
                <span className="text-[13px]" style={{ color: '#101828' }}>
                  <b style={{ color: won ? '#166534' : '#0369A1' }}>{i + 1}.</b> {r.name}
                </span>
                <span className="flex items-center gap-2.5">
                  {won && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: 'white', color: '#166534', border: '1px solid #BBF7D0' }}>
                      Ученик закреплён
                    </span>
                  )}
                  <span className="text-[12px]" style={{ color: '#98A2B3' }}>
                    {won
                      ? `${offer.assigned_by ? offer.assigned_by + ' · ' : ''}${humanMoment(offer.taken_at)}`
                      : humanMoment(r.created_at)}
                  </span>
                  {canAssign && (
                    <button onClick={() => setConfirmFor({ mk_id: r.mk_id, name: r.name })}
                      className="text-[12px] font-semibold px-2.5 py-1.5 rounded-lg"
                      style={{ background: '#F0F9FF', color: '#0369A1' }}>
                      Отдать этому
                    </button>
                  )}
                </span>
              </div>
            )
          })}
          {canAssign && (
            <div className="text-[11px] mt-2" style={{ color: '#98A2B3' }}>
              Порядок — по времени нажатия. Кому отдать ученика, решаете вы.
            </div>
          )}
        </div>
      )}

      {confirmFor && canAssign && (
        <div className="mt-3 px-4 py-3 rounded-xl"
             style={{ background: '#F0F9FF', border: '1px solid #BAE6FD' }}>
          <div className="text-[13px] mb-2.5" style={{ color: '#101828', lineHeight: 1.55 }}>
            Отдать ученика{offer.student_name ? <> <b>{offer.student_name}</b></> : ''} преподавателю{' '}
            <b>{confirmFor.name}</b>? Он получит уведомление, что ученик закреплён за ним.
            Отменить это потом нельзя.
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={async () => {
                const ok = await onAssign(offer.id, confirmFor.mk_id)
                if (ok) setConfirmFor(null)
              }}
              disabled={busyId === offer.id}
              className="text-[13px] font-bold px-4 py-2 rounded-lg disabled:opacity-50"
              style={{ background: '#38BDF8', color: 'white' }}>
              {busyId === offer.id ? 'Отдаём…' : 'Да, отдать'}
            </button>
            <button onClick={() => setConfirmFor(null)}
              className="text-[13px] font-semibold px-4 py-2 rounded-lg"
              style={{ background: 'white', color: '#667085', border: '1px solid #E4E7EC' }}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function BirzhaTab({ refreshKey }) {
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [onlyMine, setOnlyMine] = useState(true)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const r = await api.get('/api/exchange/offers', { params: { mine: onlyMine ? 1 : 0, limit: 50 } })
      setOffers(r.data.offers || [])
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить заявки')
    } finally {
      setLoading(false)
    }
  }, [onlyMine])

  useEffect(() => { load() }, [load, refreshKey])

  const cancel = async (id) => {
    setBusyId(id)
    try {
      await api.post(`/api/exchange/offers/${id}/cancel`)
      await load()
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось снять заявку')
    } finally {
      setBusyId(null)
    }
  }

  // Отдать ученика. true — получилось (карточка перерисуется отданной).
  // На 409 (другой менеджер успел раньше) просто перезагружаем список —
  // станет видно, кому досталось.
  const assign = async (offerId, teacherMkId) => {
    setBusyId(offerId)
    try {
      await api.post(`/api/exchange/offers/${offerId}/assign`, { teacher_mk_id: teacherMkId })
      await load()
      return true
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось отдать ученика')
      await load()
      return false
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="rounded-2xl p-5" style={{ border: '1px solid #E4E7EC', background: 'white' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[16px] font-bold" style={{ color: '#101828' }}>Заявки на бирже</div>
          <div className="text-[12px] mt-0.5" style={{ color: '#667085' }}>
            Заявка висит сутки и копит отклики. Ученик достаётся тому, кого выберете вы.
          </div>
        </div>
        <div className="flex gap-1.5 p-1 rounded-xl" style={{ border: '1px solid #E4E7EC' }}>
          <button onClick={() => setOnlyMine(true)}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg"
            style={onlyMine ? { background: '#F0F9FF', color: '#0369A1' } : { color: '#667085' }}>
            Мои
          </button>
          <button onClick={() => setOnlyMine(false)}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg"
            style={!onlyMine ? { background: '#F0F9FF', color: '#0369A1' } : { color: '#667085' }}>
            Все
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl text-[13px] mb-3"
             style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-[13px] py-8 text-center" style={{ color: '#98A2B3' }}>Загружаем…</div>
      ) : offers.length === 0 ? (
        <div className="text-[13px] py-10 text-center" style={{ color: '#98A2B3' }}>
          {onlyMine
            ? 'Вы пока не отдавали учеников на биржу. Кнопка «Отдать на биржу» — в карточке клиента.'
            : 'Заявок пока нет.'}
        </div>
      ) : (
        offers.map(o => <OfferCard key={o.id} offer={o} onCancel={cancel} onAssign={assign} busyId={busyId} />)
      )}
    </div>
  )
}
